import { inject, Injectable } from '@angular/core';
import { GalaxyDataSource, PagedListRequestInterface, PagedResponseInterface } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { Observable, of, shareReplay, switchMap } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Template } from '@galaxy/email-ui/email-library';
import {
  TemplatesService,
  OwnerType,
  AppNamespace,
  SearchEmailBuilderTemplatesResponseInterface,
} from '@vendasta/email-builder';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { AccountGroupService } from '../../account-group';
import { EmailTemplateFilterService } from './email-template-filter.service';

export interface EmailTemplateRow {
  id: string;
  data: {
    name: { cellType: 'text'; value: string };
  };
  template: Template;
  ownerType: OwnerType;
  ownerId: string;
}

export interface EmailTemplateServiceInterface {
  getDataSource(): GalaxyDataSource<EmailTemplateRow, GalaxyFilterInterface>;
  getColumns$(): Observable<GalaxyColumnDef[]>;
}

export function toEmailTemplateRows(response: SearchEmailBuilderTemplatesResponseInterface): EmailTemplateRow[] {
  return (response.templates ?? []).map(
    (t): EmailTemplateRow => ({
      id: t.templateId,
      data: { name: { cellType: 'text', value: t.templateDisplayName } },
      template: {
        templateId: t.templateId,
        title: t.templateDisplayName,
        previewUrl: t.previewUrl,
        created: t.created,
        updated: t.updated,
        templateJson: t.templateJson ?? '{}',
        templateSubject: t.templateSubject ?? '',
        templateHtml: t.templateHtml,
        editorUrl: '',
      },
      ownerType: t.ownerInfo.ownerType,
      ownerId: t.ownerInfo.ownerId,
    }),
  );
}

@Injectable({ providedIn: 'root' })
export class EmailTemplateService implements EmailTemplateServiceInterface {
  private readonly templatesService = inject(TemplatesService);
  private readonly accountGroupService = inject(AccountGroupService);
  private readonly filterService = inject(EmailTemplateFilterService);
  private readonly namespace$ = this.accountGroupService.currentAccountGroupId$.pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  getDataSource(): GalaxyDataSource<EmailTemplateRow, GalaxyFilterInterface> {
    const paginatedAPI = {
      get: (req: PagedListRequestInterface<GalaxyFilterInterface>): Observable<PagedResponseInterface<EmailTemplateRow>> =>
        this.namespace$.pipe(
          switchMap((namespace) => {
            const owners = this.filterService.resolveOwners(req.filters ?? [], namespace);
            return this.templatesService.searchTemplates(
              null,
              '',
              AppNamespace.EMAIL_LIBRARY,
              req.searchOptions?.text ?? '',
              req.pagingOptions?.pageSize ?? 12,
              req.pagingOptions?.cursor,
              owners,
            );
          }),
          map((response) => ({
            data: toEmailTemplateRows(response),
            pagingMetadata: {
              nextCursor: response.pagingMetadata?.nextCursor ?? '',
              hasMore: response.pagingMetadata?.hasMore ?? false,
              totalResults: response.hits,
            },
          })),
        ),
    };

    return new GalaxyDataSource<EmailTemplateRow, GalaxyFilterInterface>(paginatedAPI);
  }

  getColumns$(): Observable<GalaxyColumnDef[]> {
    return of([{ id: 'name', title: 'Name', sortable: false }]);
  }
}
